//region header */
//--------------------------------------------------------------------------------------------------
//
//  Description: Property Details Tab
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier            Issue# Description
//region Version 1.0.0.0
//    001   20.07.21 Sean Flook         WI39??? Initial Revision.
//    002   22.03.23 Sean Flook         WI40596 Only allow editing if BLPU logical status is not historic or rejected.
//    003   27.03.23 Sean Flook         WI40627 Prevent useEffect from being run when not required.
//    004   28.03.23 Sean Flook         WI40596 Removed above change.
//    005   26.04.23 Sean Flook         WI40700 Do not set end date when deleting.
//    006   28.06.23 Sean Flook         WI40256 Changed Extent to Provenance where appropriate.
//    007   27.10.23 Sean Flook                 Use new dataFormStyle.
//    008   24.11.23 Sean Flook                 Moved Box and Stack to @mui/system.
//    009   05.01.24 Sean Flook                 Changes to sort out warnings.
//    010   11.01.24 Sean Flook                 Fix warnings.
//    011   16.01.24 Sean Flook                 Changes required to fix warnings.
//    012   09.02.24 Sean Flook                 Added id to box so that ADSSelectionControl can use it.
//    013   11.03.24 Sean Flook           GLB12 Adjusted height to remove gap.
//    014   13.03.24 Joshua McCormick IMANN-280 Added dataTabToolBar for inner toolbar styling
//    015   18.03.24 Sean Flook           GLB12 Adjusted height to remove overflow.
//    016   18.03.24 Sean Flook      STRFRM4_OS Set the nullString parameter for the key.
//    017   22.03.24 Sean Flook           GLB12 Changed to use dataFormStyle so height can be correctly set.
//    018   22.03.24 Sean Flook       PRFRM5_GP Display an information control for creating and modifying the geometry.
//    019   09.04.24 Joshua McCormick IMANN-277 Added displayCharactersLeft for inputs that it should be shown for
//    020   29.04.24 Joshua McCormick IMANN-386 Toolbar changes no title no wrapping with width restrictions
//    021   20.06.24 Sean Flook       IMANN-636 Use the new user rights.
//    022   26.07.24 Sean Flook       IMANN-856 Correctly handle deleting newly added record.
//endregion Version 1.0.0.0
//region Version 1.0.5.0
//    023   23.04.25 Sean Flook       IMANN-1768 Changes required for selecting properties in property and street forms.
//    024   24.04.25 Sean Flook       IMANN-1760 Prevent multiple renders from occurring.
//endregion Version 1.0.5.0
//region Version 1.0.7.0
//    025   13.06.25 Sean Flook       IMANN-1812 Modified to use the new Sandbox store.
//    026   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    027   28.10.25 Sean Flook       IMANN-2112 Always keep the OK and Cancel buttons at the bottom of the tab.
//    028   28.10.25 Sean Flook       IMANN-2105 When the save is disabled disable the Ok button.
//    029   05.12.25 Sean Flook       IMANN-1671 Various fixes for handling polygons.
//    030   17.12.25 Sean Flook       IMANN-2196 Removed the information component as no longer required in this control.
//    031   27.01.26 Joshua McCormick IMANN-2221 Hydration errors rendering typography
//endregion Version 1.0.7.0
//region Version 1.0.7.2
//    032   01.05.26 Sean Flook       IMANN-2429 Added saveDisabled parameter to ensure the OK button is correctly enabled/disabled.
//endregion Version 1.0.7.2
//
//--------------------------------------------------------------------------------------------------
//endregion header */

import React, { useContext, useState, useRef, useEffect, Fragment } from "react";
import PropTypes from "prop-types";

import useSandboxStore from "../context/SandboxStore";

import UserContext from "../context/userContext";
import PropertyContext from "../context/propertyContext";
import SettingsContext from "../context/settingsContext";
import InformationContext from "../context/informationContext";
import MapContext from "../context/mapContext";

import { GetLookupLabel, ConvertDate, filteredLookup, UpdateSandbox } from "../utils/HelperUtils";

import { Typography, Box, Stack } from "@mui/material";

import ADSActionButton from "../components/ADSActionButton";
import ADSSelectControl from "../components/ADSSelectControl";
import ADSTextControl from "../components/ADSTextControl";
import ADSDateControl from "../components/ADSDateControl";
import ADSReadOnlyControl from "../components/ADSReadOnlyControl";
import ADSOkCancelControl from "../components/ADSOkCancelControl";

import ConfirmDeleteDialog from "../dialogs/ConfirmDeleteDialog";

import BLPUProvenance from "../data/BLPUProvenance";

import ErrorIcon from "@mui/icons-material/Error";

import { toolbarStyle, dataFormStyle, errorIconStyle } from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";

PropertyBLPUProvenanceTab.propTypes = {
  data: PropTypes.object,
  errors: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  focusedField: PropTypes.string,
  saveDisabled: PropTypes.bool,
  onDataChanged: PropTypes.func.isRequired,
  onHomeClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function PropertyBLPUProvenanceTab({
  data,
  errors,
  loading,
  focusedField,
  saveDisabled,
  onDataChanged,
  onHomeClick,
  onDelete,
}) {
  const theme = useTheme();

  const sandboxStore = useSandboxStore((state) => state);
  const sourceProperty = useSandboxStore((state) => state.sourceProperty);
  const currentProvenanceRecord = useSandboxStore((state) => state.currentProvenanceRecord);
  const setStore = useSandboxStore((state) => state.setStore);

  const userContext = useContext(UserContext);
  const propertyContext = useContext(PropertyContext);
  const settingsContext = useContext(SettingsContext);
  const informationContext = useContext(InformationContext);
  const mapContext = useContext(MapContext);

  const currentId = useRef(0);

  const [provenanceLookup, setProvenanceLookup] = useState(filteredLookup(BLPUProvenance, settingsContext.isScottish));

  const [code, setCode] = useState(null);
  const [annotation, setAnnotation] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [userCanEdit, setUserCanEdit] = useState(false);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const [codeError, setCodeError] = useState(null);
  const [annotationError, setAnnotationError] = useState(null);
  const [startDateError, setStartDateError] = useState(null);
  const [endDateError, setEndDateError] = useState(null);

  /**
   * Method used to update the current sandbox record.
   *
   * @param {string} field The name of the field that is being updated.
   * @param {string|boolean|Date|number|null} newValue The value used to update the given field.
   */
  const RefreshSandboxData = (field, newValue) => {
    const newProvenance = GetCurrentData(field, newValue);
    setStore(UpdateSandbox(sandboxStore, "provenance", newProvenance));
  };

  /**
   * Event to handle when the code is changed.
   *
   * @param {string|null} newValue The new code.
   */
  const handleCodeChangeEvent = (newValue) => {
    setCode(newValue);
    if (!propertyContext.provenanceDataChanged) {
      propertyContext.onProvenanceDataChange(code !== newValue);
      if (onDataChanged && code !== newValue) onDataChanged();
    }
    RefreshSandboxData("provenanceCode", newValue);
  };

  /**
   * Event to handle when the annotation is changed.
   *
   * @param {string|null} newValue The new annotation.
   */
  const handleAnnotationChangeEvent = (newValue) => {
    setAnnotation(newValue);
    if (!propertyContext.provenanceDataChanged) {
      propertyContext.onProvenanceDataChange(annotation !== newValue);
      if (onDataChanged && annotation !== newValue) onDataChanged();
    }
    RefreshSandboxData("annotation", newValue);
  };

  /**
   * Event to handle when the start date is changed.
   *
   * @param {Date|null} newValue The new start date.
   */
  const handleStartDateChangeEvent = (newValue) => {
    setStartDate(newValue);
    if (!propertyContext.provenanceDataChanged) {
      propertyContext.onProvenanceDataChange(startDate !== newValue);
      if (onDataChanged && startDate !== newValue) onDataChanged();
    }
    RefreshSandboxData("startDate", newValue);
  };

  /**
   * Event to handle when the end date is changed.
   *
   * @param {Date|null} newValue The new end date.
   */
  const handleEndDateChangeEvent = (newValue) => {
    setEndDate(newValue);
    if (!propertyContext.provenanceDataChanged) {
      propertyContext.onProvenanceDataChange(endDate !== newValue);
      if (onDataChanged && endDate !== newValue) onDataChanged();
    }
    RefreshSandboxData("endDate", newValue);
  };

  /**
   * Event to handle when the home button is clicked.
   */
  const handleHomeClick = () => {
    const sourceProvenance =
      data.id > 0 && sourceProperty ? sourceProperty.blpuProvenances.find((x) => x.pkId === data.id) : null;

    propertyContext.onProvenanceDataChange(
      onHomeClick(
        propertyContext.provenanceDataChanged ? (currentProvenanceRecord ? "check" : "discard") : "discard",
        sourceProvenance,
        currentProvenanceRecord
      )
    );
  };

  /**
   * Event to handle when the user selects to delete the provenance.
   */
  const handleDeleteClick = () => {
    setOpenDeleteConfirmation(true);
  };

  /**
   * Event to handle when the OK button is clicked.
   */
  const handleOkClicked = () => {
    mapContext.onRefreshPolygonGeometry();
    propertyContext.onProvenanceDataChange(onHomeClick("save", null, currentProvenanceRecord));
  };

  /**
   * Event to handle when the Cancel button is clicked.
   */
  const handleCancelClicked = () => {
    if (propertyContext.provenanceDataChanged) {
      if (data && data.provenanceData) {
        setCode(data.provenanceData.provenanceCode);
        setAnnotation(data.provenanceData.annotation ? data.provenanceData.annotation : "");
        setStartDate(data.provenanceData.startDate);
        setEndDate(data.provenanceData.endDate);
      }
    }
    propertyContext.onProvenanceDataChange(false);
    if (onHomeClick) onHomeClick("discard", data.provenanceData, null);
  };

  /**
   * Event to handle when the delete confirmation dialog is closed.
   *
   * @param {boolean} deleteConfirmed True if the user has confirmed the delete; otherwise false.
   */
  const handleCloseDeleteConfirmation = (deleteConfirmed) => {
    setOpenDeleteConfirmation(false);
    const id = data && data.id ? data.id : -1;

    if (deleteConfirmed && id) {
      const currentData = GetCurrentData("changeType", "D");
      if (onHomeClick) onHomeClick("save", null, currentData);
      if (onDelete) onDelete(id);
    }
  };

  /**
   * Method to get the text for a given provenance code.
   *
   * @param {string|null} code The provenance code we want the text for.
   * @returns {string} The text for the given provenance code.
   */
  function getProvenanceText(code) {
    const rec = BLPUProvenance.find((x) => x.id === code);

    if (rec) return rec[GetLookupLabel(settingsContext.isScottish)];
    else return "";
  }

  /**
   * Method to return the current provenance record.
   *
   * @param {string} field The name of the field that is being updated.
   * @param {string|Date|null} newValue The value used to update the given field.
   * @returns {object} The current provenance record.
   */
  function GetCurrentData(field, newValue) {
    return {
      changeType: field && field === "changeType" ? newValue : !data.provenanceData.provenanceKey ? "I" : "U",
      annotation: field && field === "annotation" ? newValue : annotation,
      endDate: field && field === "endDate" ? newValue && ConvertDate(newValue) : endDate && ConvertDate(endDate),
      entryDate: data.provenanceData.entryDate,
      pkId: data.provenanceData.id,
      lastUpdateDate: data.provenanceData.lastUpdateDate,
      provenanceCode: field && field === "provenanceCode" ? newValue : code,
      provenanceKey: data.provenanceData.provenanceKey,
      wktGeometry: data.provenanceData.wktGeometry,
      startDate:
        field && field === "startDate" ? newValue && ConvertDate(newValue) : startDate && ConvertDate(startDate),
      uprn: data.provenanceData.uprn,
    };
  }

  useEffect(() => {
    if (data && data.provenanceData) {
      setCode(data.provenanceData.provenanceCode);
      setAnnotation(data.provenanceData.annotation ? data.provenanceData.annotation : "");
      setStartDate(data.provenanceData.startDate);
      setEndDate(data.provenanceData.endDate);

      setProvenanceLookup(filteredLookup(BLPUProvenance, settingsContext.isScottish));
    }
  }, [data, settingsContext.isScottish]);

  useEffect(() => {
    if (data && data.id < 0 && !currentProvenanceRecord && currentId.current !== data.id) {
      currentId.current = data.id;
    }
  }, [data, currentProvenanceRecord]);

  useEffect(() => {
    if (userCanEdit !== (userContext.currentUser && userContext.currentUser.editProperty))
      setUserCanEdit(userContext.currentUser && userContext.currentUser.editProperty);
  }, [userContext.currentUser, userCanEdit]);

  useEffect(() => {
    if (
      data &&
      data.provenanceData &&
      data.provenanceData.id &&
      (!informationContext.informationType ||
        (informationContext.informationType === "createPolygon" && data.provenanceData.id > 0) ||
        (informationContext.informationType === "managePolygon" && data.provenanceData.id < 0))
    ) {
      informationContext.onDisplayInformation(
        data.provenanceData.id < 0 ? "createPolygon" : "managePolygon",
        "PropertyBLPUProvenanceTab"
      );
    }
  }, [data, informationContext]);

  useEffect(() => {
    setCodeError(null);
    setAnnotationError(null);
    setStartDateError(null);
    setEndDateError(null);

    if (errors && errors.length > 0) {
      for (const error of errors) {
        switch (error.field.toLowerCase()) {
          case "provenancecode":
            setCodeError(error.errors);
            break;

          case "annotation":
            setAnnotationError(error.errors);
            break;

          case "startdate":
            setStartDateError(error.errors);
            break;

          case "enddate":
            setEndDateError(error.errors);
            break;

          default:
            break;
        }
      }
    }
  }, [errors]);

  useEffect(() => {
    if (saveDisabled && propertyContext.provenanceDataChanged) propertyContext.onProvenanceDataChange(false);
  }, [saveDisabled, propertyContext]);

  return (
    <Fragment>
      <Box sx={toolbarStyle} id="ads-property-provenance-tab">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: theme.spacing(0.25) }}>
          <Typography variant="subtitle1">
            <ADSActionButton variant="home" tooltipTitle="Home" tooltipPlacement="bottom" onClick={handleHomeClick} />
          </Typography>
          <Typography
            sx={{
              flexGrow: 1,
              fontSize: "14px",
              display: "none",
              pl: "6px",
              pt: "2px",
              [theme.breakpoints.up("sm")]: {
                display: "block",
              },
            }}
            variant="subtitle1"
            noWrap
            align="left"
          >
            {`| BLPU provenance (${data.index + 1} of ${data.totalRecords}): ${getProvenanceText(code)}`}
          </Typography>
          {errors && errors.length > 0 && <ErrorIcon sx={errorIconStyle} />}
        </Stack>
        <ADSActionButton
          variant="delete"
          disabled={!userCanEdit}
          tooltipTitle="Delete"
          tooltipPlacement="right"
          onClick={handleDeleteClick}
        />
      </Box>
      <Box sx={dataFormStyle("PropertyBLPUProvenanceTab")}>
        <ADSSelectControl
          label="Provenance"
          isEditable={userCanEdit}
          isRequired
          isFocused={focusedField ? focusedField === "ProvenanceCode" : false}
          loading={loading}
          useRounded
          doNotSetTitleCase
          lookupData={provenanceLookup}
          lookupId="id"
          lookupLabel={GetLookupLabel(settingsContext.isScottish)}
          lookupColour="colour"
          lookupIcon="avatar_icon"
          value={code}
          errorText={codeError}
          onChange={handleCodeChangeEvent}
          helperText="Identifies the BLPU Provenance Extent derivation."
        />
        <ADSTextControl
          label="Annotation"
          isEditable={userCanEdit}
          isFocused={focusedField ? focusedField === "Annotation" : false}
          loading={loading}
          value={annotation}
          id="annotation"
          maxLength={30}
          displayCharactersLeft
          errorText={annotationError}
          helperText="Supplementary information to support the provenance."
          onChange={handleAnnotationChangeEvent}
        />
        <ADSDateControl
          label="Start date"
          isEditable={userCanEdit}
          isRequired
          isFocused={focusedField ? focusedField === "StartDate" : false}
          loading={loading}
          value={startDate}
          helperText="Date of start of this Provenance Record."
          errorText={startDateError}
          onChange={handleStartDateChangeEvent}
        />
        <ADSDateControl
          label="End date"
          isEditable={userCanEdit}
          isFocused={focusedField ? focusedField === "EndDate" : false}
          loading={loading}
          value={endDate}
          helperText="Date of end of this Provenance Record."
          errorText={endDateError}
          onChange={handleEndDateChangeEvent}
        />
        <ADSReadOnlyControl
          label="Provenance key"
          loading={loading}
          value={data.provenanceData.provenanceKey}
          nullString="Key set on save"
        />
      </Box>
      <ADSOkCancelControl
        okDisabled={!propertyContext.provenanceDataChanged}
        onOkClicked={handleOkClicked}
        onCancelClicked={handleCancelClicked}
      />
      <div>
        <ConfirmDeleteDialog
          variant="provenance"
          open={openDeleteConfirmation}
          onClose={handleCloseDeleteConfirmation}
        />
      </div>
    </Fragment>
  );
}

export default PropertyBLPUProvenanceTab;
